import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Data Explorer Controller
 *
 * Handles interactive data exploration features:
 * - Column visibility toggling
 * - Sorting by column
 * - Filtering with modal
 * - URL state management
 */
export default class extends Controller {
  static targets = [
    "tableContainer",
    "filterModal",
    "filterField",
    "filterOperator",
    "filterValue"
  ]

  static values = {
    metricId: Number,
    dashboardId: Number,
    columns: Array,
    visibleColumns: Array
  }

  connect() {
    this.currentFilters = this.parseFiltersFromUrl()
  }

  // ==========================================
  // Column Visibility
  // ==========================================

  toggleColumn(event) {
    const checkbox = event.currentTarget
    const columnKey = checkbox.dataset.columnKey
    const isVisible = checkbox.checked

    let visibleColumns = [...this.visibleColumnsValue]

    if (isVisible) {
      if (!visibleColumns.includes(columnKey)) {
        visibleColumns.push(columnKey)
      }
    } else {
      visibleColumns = visibleColumns.filter(c => c !== columnKey)
    }

    this.visibleColumnsValue = visibleColumns
    this.updateUrlAndReload({ columns: visibleColumns.join(',') })
  }

  showAllColumns() {
    const allColumns = this.columnsValue.map(c => c.key)
    this.visibleColumnsValue = allColumns
    this.updateUrlAndReload({ columns: allColumns.join(',') })
  }

  hideAllColumns() {
    // Keep at least the first column visible
    const firstColumn = this.columnsValue[0]?.key
    this.visibleColumnsValue = firstColumn ? [firstColumn] : []
    this.updateUrlAndReload({ columns: firstColumn || '' })
  }

  // ==========================================
  // Sorting
  // ==========================================

  sortByColumn(event) {
    const columnKey = event.currentTarget.dataset.columnKey
    const url = new URL(window.location.href)
    const currentSort = url.searchParams.get('sort')
    const currentDirection = url.searchParams.get('direction')

    let newDirection = 'asc'
    if (currentSort === columnKey) {
      newDirection = currentDirection === 'asc' ? 'desc' : 'asc'
    }

    this.updateUrlAndReload({
      sort: columnKey,
      direction: newDirection,
      page: 1 // Reset to first page when sorting
    })
  }

  // ==========================================
  // Filtering
  // ==========================================

  openFilterModal() {
    if (this.hasFilterModalTarget) {
      // Reset form
      if (this.hasFilterFieldTarget) this.filterFieldTarget.value = ''
      if (this.hasFilterOperatorTarget) this.filterOperatorTarget.value = 'eq'
      if (this.hasFilterValueTarget) this.filterValueTarget.value = ''
      this.filterModalTarget.showModal()
    }
  }

  closeFilterModal() {
    if (this.hasFilterModalTarget) {
      this.filterModalTarget.close()
    }
  }

  applyFilter() {
    const field = this.hasFilterFieldTarget ? this.filterFieldTarget.value : ''
    const operator = this.hasFilterOperatorTarget ? this.filterOperatorTarget.value : 'eq'
    const value = this.hasFilterValueTarget ? this.filterValueTarget.value : ''

    if (!field) {
      alert(i18n.t('data_explorer.select_a_field'))
      return
    }

    // Add new filter
    this.currentFilters.push({ field, operator, value })

    this.closeFilterModal()
    this.updateUrlAndReload({
      filters: JSON.stringify(this.currentFilters),
      page: 1 // Reset to first page when filtering
    })
  }

  removeFilter(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (isNaN(index)) return

    this.currentFilters.splice(index, 1)

    this.updateUrlAndReload({
      filters: this.currentFilters.length > 0 ? JSON.stringify(this.currentFilters) : null,
      page: 1
    })
  }

  clearFilters() {
    this.currentFilters = []
    this.updateUrlAndReload({
      filters: null,
      page: 1
    })
  }

  parseFiltersFromUrl() {
    const url = new URL(window.location.href)
    const filtersParam = url.searchParams.get('filters')

    if (!filtersParam) return []

    try {
      return JSON.parse(filtersParam)
    } catch {
      return []
    }
  }

  // ==========================================
  // URL Management
  // ==========================================

  updateUrlAndReload(params) {
    const url = new URL(window.location.href)

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        url.searchParams.delete(key)
      } else {
        url.searchParams.set(key, value)
      }
    })

    // Use Turbo to navigate
    Turbo.visit(url.toString(), { action: 'replace' })
  }
}
